import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import type { SafetyKakaoSettings } from '../../constants/safetyKakaoSettings';

type Props = {
  value: SafetyKakaoSettings;
  onChange: (next: SafetyKakaoSettings) => void;
  onSave: () => void;
  onResetDefaults: () => void;
  saving?: boolean;
  cardSx?: SxProps<Theme>;
};

const SafetyKakaoSettingsCard: React.FC<Props> = ({
  value,
  onChange,
  onSave,
  onResetDefaults,
  saving = false,
  cardSx,
}) => (
  <Card sx={cardSx}>
    <CardHeader
      title="카카오 알림톡"
      subheader="통합 관제 경고/위험 시 카카오 비즈메시지. 수신 번호는 SMS와 동일(safety_phone)."
      action={
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={onResetDefaults}
            disabled={saving}
          >
            기본값
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={onSave}
            disabled={saving}
          >
            {saving ? '저장 중…' : '저장'}
          </Button>
        </Box>
      }
    />
    <CardContent>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={value.default_selected}
                onChange={(e) => onChange({ ...value, default_selected: e.target.checked })}
              />
            }
            label="관제 팝업에서 기본 선택"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth margin="dense">
            <InputLabel>발송 방식</InputLabel>
            <Select
              label="발송 방식"
              value={value.provider}
              onChange={(e) =>
                onChange({
                  ...value,
                  provider: e.target.value as SafetyKakaoSettings['provider'],
                })
              }
            >
              <MenuItem value="auto">자동 (알리고 → 웹훅 → 로그)</MenuItem>
              <MenuItem value="aligo">알리고 알림톡</MenuItem>
              <MenuItem value="webhook">웹훅</MenuItem>
              <MenuItem value="log">로그만</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Switch
                checked={value.use_sms_aligo_credentials}
                onChange={(e) =>
                  onChange({ ...value, use_sms_aligo_credentials: e.target.checked })
                }
              />
            }
            label="SMS 알리고 API KEY·USER ID 공유"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            margin="dense"
            label="HTTP 타임아웃(초)"
            type="number"
            value={value.timeout_sec}
            onChange={(e) =>
              onChange({ ...value, timeout_sec: Number(e.target.value) || 12 })
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary">
            알리고 알림톡
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            margin="dense"
            label="알림톡 API URL"
            value={value.aligo_api_url}
            onChange={(e) => onChange({ ...value, aligo_api_url: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            margin="dense"
            label="API KEY (비우면 SMS 설정 사용)"
            value={value.aligo_api_key}
            onChange={(e) => onChange({ ...value, aligo_api_key: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            margin="dense"
            label="USER ID"
            value={value.aligo_user_id}
            onChange={(e) => onChange({ ...value, aligo_user_id: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            margin="dense"
            label="발신 프로필 senderkey"
            value={value.aligo_sender_key}
            onChange={(e) => onChange({ ...value, aligo_sender_key: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            margin="dense"
            label="템플릿 코드 (경고)"
            value={value.tpl_code_warning}
            onChange={(e) => onChange({ ...value, tpl_code_warning: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            margin="dense"
            label="템플릿 코드 (위험)"
            value={value.tpl_code_danger}
            onChange={(e) => onChange({ ...value, tpl_code_danger: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            margin="dense"
            label="알림 제목"
            value={value.subject_template}
            onChange={(e) => onChange({ ...value, subject_template: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Switch
                checked={value.aligo_test_mode}
                onChange={(e) => onChange({ ...value, aligo_test_mode: e.target.checked })}
              />
            }
            label="알리고 테스트 모드"
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary">
            웹훅
          </Typography>
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            margin="dense"
            label="웹훅 URL"
            value={value.webhook_url}
            onChange={(e) => onChange({ ...value, webhook_url: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth margin="dense">
            <InputLabel>웹훅 형식</InputLabel>
            <Select
              label="웹훅 형식"
              value={value.webhook_format}
              onChange={(e) =>
                onChange({
                  ...value,
                  webhook_format: e.target.value as SafetyKakaoSettings['webhook_format'],
                })
              }
            >
              <MenuItem value="generic">JSON (phone, message, level)</MenuItem>
              <MenuItem value="aligo_alimtalk">알리고 알림톡 form</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={value.webhook_batch}
                onChange={(e) => onChange({ ...value, webhook_batch: e.target.checked })}
              />
            }
            label="웹훅 배치(한 번에 여러 수신자)"
          />
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);

export default SafetyKakaoSettingsCard;
